import {
	Alert,
	Autocomplete,
	Box,
	Chip,
	CircularProgress,
	Snackbar,
	TextField,
	Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import { ContactType, useInitializeDocumentEmailLazyQuery } from "@repo/server-api";
import type { Invoice, CreditNote } from "../types/CommercialDocumentTypes";
import { BoundTextField, useAsyncSelectMembersWithEmailQuery } from "@repo/shared/components";
import { useDebounce } from "@repo/shared/common";
import { useTranslation } from "next-i18next";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import {
	PDFAttachments,
	type PDFAttachment,
	initializeExistingAttachments,
	blobToBase64,
} from "../components/PDFAttachments";

import "tinymce/tinymce";
import "tinymce/models/dom/model";
import "tinymce/themes/silver/theme";
import "tinymce/icons/default/icons";
import "tinymce/skins/ui/oxide/skin.min.css";
import "tinymce/plugins/advlist";
import "tinymce/plugins/autolink";
import "tinymce/plugins/lists";
import "tinymce/plugins/link";
import "tinymce/plugins/image";
import "tinymce/plugins/charmap";
import "tinymce/plugins/anchor";
import "tinymce/plugins/searchreplace";
import "tinymce/plugins/visualblocks";
import "tinymce/plugins/code";
import "tinymce/plugins/fullscreen";
import "tinymce/plugins/insertdatetime";
import "tinymce/plugins/media";
import "tinymce/plugins/table";
import "tinymce/plugins/preview";
import "tinymce/plugins/wordcount";

import { Editor } from "@tinymce/tinymce-react";

export type EmailAttachmentPayload = {
	name: string;
	content: string; // base64 encoded
	extension: string;
	positionNumber: number;
};

export type EmailSendData = {
	recipients: string[]; // email addresses
	ccRecipients: string[]; // CC email addresses
	bccRecipients: string[]; // BCC email addresses
	subject: string;
	body: string;
	attachments: EmailAttachmentPayload[];
	shouldMergeAttachments: boolean;
};

export type EmailDialogShowArgs = {
	invoice: Invoice | CreditNote;
	id: string;
	entityId?: string | null;
	existingAttachments?: PDFAttachment[];
	onSend: (data: EmailSendData) => void;
	className?: string;
};

export type EmailDialogHandle = {
	show: (args: EmailDialogShowArgs) => void;
	close: () => void;
};

export const EmailDialog = forwardRef<EmailDialogHandle>((_props, ref) => {
	const { t } = useTranslation();

	const [open, setOpen] = useState(false);
	const [options, setOptions] = useState<EmailDialogShowArgs | null>(null);
	const [recipientEmails, setRecipientEmails] = useState<string[]>([]);
	const [recipientInputValue, setRecipientInputValue] = useState("");
	const [ccEmails, setCcEmails] = useState<string[]>([]);
	const [ccInputValue, setCcInputValue] = useState("");
	const [bccEmails, setBccEmails] = useState<string[]>([]);
	const [bccInputValue, setBccInputValue] = useState("");

	const [allAttachments, setAllAttachments] = useState<PDFAttachment[]>([]);
	const [shouldMergeAttachments, setShouldMergeAttachments] = useState(false);
	const [backendAttachments, setBackendAttachments] = useState<PDFAttachment[]>([]);
	const [isSending, setIsSending] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const [isLoadingData, setIsLoadingData] = useState(false);

	const id = options?.id ?? "";
	const entityId = options?.entityId ?? undefined;
	const invoice = options?.invoice ?? null;
	const clientId = invoice?.client?.id ?? null;

	const existingAttachments = useMemo(
		() => [...(options?.existingAttachments ?? []), ...backendAttachments],
		[options?.existingAttachments, backendAttachments],
	);

	const searchEmailsHook = useAsyncSelectMembersWithEmailQuery(clientId, [
		ContactType.ContactPerson,
	]);
	const debouncedRecipientSearch = useDebounce(recipientInputValue, 300);
	const debouncedCcSearch = useDebounce(ccInputValue, 300);
	const debouncedBccSearch = useDebounce(bccInputValue, 300);

	// Combine all search terms to trigger a single query
	const combinedSearchTerm = [debouncedRecipientSearch, debouncedCcSearch, debouncedBccSearch]
		.filter(Boolean)
		.join(" ");

	// Load search results when any input changes
	useEffect(() => {
		void searchEmailsHook.execute(combinedSearchTerm || debouncedRecipientSearch, null);
	}, [combinedSearchTerm, debouncedRecipientSearch, searchEmailsHook.execute]);

	// Set default recipient from invoice.clientContactPersonId when items are loaded
	useEffect(() => {
		if (!open || recipientEmails.length > 0) return;

		const contactPersonId = invoice?.contactPerson?.id;
		const existingRecipient = searchEmailsHook.items.find((item) => item.id === contactPersonId);

		if (existingRecipient?.email) {
			setRecipientEmails([existingRecipient.email]);
		}
	}, [open, invoice?.contactPerson?.id, searchEmailsHook.items, recipientEmails.length]);

	const [getDocumentEmailData] = useInitializeDocumentEmailLazyQuery({ fetchPolicy: "no-cache" });

	const [invoiceData, setInvoiceData] = useState<{
		subject: string;
		body: string;
		attachments: EmailAttachmentPayload[];
	}>({
		subject: "",
		body: "",
		attachments: [],
	});

	type ResponseEmailType = {
		subject: string;
		body: string;
		recipients?: string[];
		ccRecipients?: string[];
		bccRecipients?: string[];
		attachments?: EmailAttachmentPayload[];
	};

	const initializeEmailDataFromResponse = (res: ResponseEmailType) => {
		if (res) {
			setInvoiceData({
				subject: res.subject,
				body: res.body,
				attachments:
					(res.attachments?.map((att: EmailAttachmentPayload, index: number) => ({
						name: att.name,
						content: att.content,
						extension: att.extension,
						positionNumber: index,
					})) as EmailAttachmentPayload[]) || ([] as EmailAttachmentPayload[]),
			});

			// Set default recipients from response
			if (res.recipients && res.recipients.length > 0) {
				setRecipientEmails(res.recipients);
			}
			if (res.ccRecipients && res.ccRecipients.length > 0) {
				setCcEmails(res.ccRecipients);
			}
			if (res.bccRecipients && res.bccRecipients.length > 0) {
				setBccEmails(res.bccRecipients);
			}

			// Convert base64 attachment strings to PDFAttachment objects
			if (res.attachments && res.attachments.length > 0) {
				const converted = initializeExistingAttachments(
					res.attachments as EmailAttachmentPayload[],
				);
				setBackendAttachments(converted);
			}
		}
		return;
	};

	useEffect(() => {
		const fetchEmailData = async () => {
			if (!id) return;

			setIsLoadingData(true);
			try {
				const { data } = await getDocumentEmailData({
					variables: { id: id, entityId: entityId },
				});
				initializeEmailDataFromResponse(data?.initializeDocumentEmail as ResponseEmailType);
			} finally {
				setIsLoadingData(false);
			}
		};

		fetchEmailData();
	}, [id, getDocumentEmailData, entityId]);

	// Cleanup URLs on unmount to prevent memory leaks
	useEffect(() => {
		return () => {
			backendAttachments.forEach((att) => {
				if (att.url) URL.revokeObjectURL(att.url);
			});
		};
	}, [backendAttachments]);

	const handleAttachmentsChange = useCallback(
		(attachments: PDFAttachment[], shouldMerge: boolean) => {
			setAllAttachments(attachments);
			setShouldMergeAttachments(shouldMerge);
		},
		[],
	);

	// Helper: Validate email format
	const isValidEmail = useCallback((email: string): boolean => {
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return emailRegex.test(email);
	}, []);

	useImperativeHandle(
		ref,
		() => ({
			show: (args: EmailDialogShowArgs) => {
				setOptions(args);
				setRecipientEmails([]);
				setRecipientInputValue("");
				setCcEmails([]);
				setCcInputValue("");
				setBccEmails([]);
				setBccInputValue("");
				setAllAttachments([]);
				setShouldMergeAttachments(false);
				setIsLoadingData(true);
				setOpen(true);
			},
			close: () => {
				handleCloseInternal();
			},
		}),
		[],
	);

	const handleCloseInternal = () => {
		// Cleanup backend attachment URLs before resetting
		backendAttachments.forEach((att) => {
			if (att.url) URL.revokeObjectURL(att.url);
		});

		setOpen(false);
		setOptions(null);
		setRecipientEmails([]);
		setRecipientInputValue("");
		setCcEmails([]);
		setCcInputValue("");
		setBccEmails([]);
		setBccInputValue("");
		setAllAttachments([]);
		setShouldMergeAttachments(false);
		setBackendAttachments([]);
		setIsSending(false);
		setInvoiceData({ subject: "", body: "", attachments: [] });
		setIsLoadingData(false);
	};

	const handleSendClick = async () => {
		if (!options?.onSend) return;

		try {
			setIsSending(true);

			// Filter out invalid emails for all fields
			const validRecipients = recipientEmails.filter((email) => isValidEmail(email));
			const validCcRecipients = ccEmails.filter((email) => isValidEmail(email));
			const validBccRecipients = bccEmails.filter((email) => isValidEmail(email));

			// Transform attachments to base64 payload format
			const attachmentPayloads = await Promise.all(
				allAttachments.map(async (attachment, index) => ({
					name: attachment.name,
					content: await blobToBase64(attachment.blob),
					extension: attachment.extension,
					positionNumber: index,
				})),
			);

			options.onSend({
				recipients: validRecipients,
				ccRecipients: validCcRecipients,
				bccRecipients: validBccRecipients,
				subject: invoiceData.subject,
				body: invoiceData.body,
				attachments: attachmentPayloads,
				shouldMergeAttachments,
			});

			handleCloseInternal();
		} catch {
			setErrorMessage(t("error-sending-email"));
			setIsSending(false);
		}
	};

	const handleCloseError = () => {
		setErrorMessage(null);
	};

	// Handle scrolling for infinite scroll in recipient dropdowns
	const handleRecipientScroll = (e: React.UIEvent<HTMLUListElement>): void => {
		const el = e.currentTarget;
		const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 20;

		if (atBottom && searchEmailsHook.hasNextPage && !searchEmailsHook.loading) {
			void searchEmailsHook.execute(
				combinedSearchTerm || debouncedRecipientSearch,
				searchEmailsHook.endCursor,
			);
		}
	};

	const handleCcScroll = (e: React.UIEvent<HTMLUListElement>): void => {
		const el = e.currentTarget;
		const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 20;

		if (atBottom && searchEmailsHook.hasNextPage && !searchEmailsHook.loading) {
			void searchEmailsHook.execute(
				combinedSearchTerm || debouncedCcSearch,
				searchEmailsHook.endCursor,
			);
		}
	};

	const handleBccScroll = (e: React.UIEvent<HTMLUListElement>): void => {
		const el = e.currentTarget;
		const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 20;

		if (atBottom && searchEmailsHook.hasNextPage && !searchEmailsHook.loading) {
			void searchEmailsHook.execute(
				combinedSearchTerm || debouncedBccSearch,
				searchEmailsHook.endCursor,
			);
		}
	};

	return (
		<Dialog
			open={open}
			data-testid="email-dialog"
			aria-describedby="email-dialog-description"
			maxWidth="md"
			fullWidth
			slotProps={{ paper: { sx: { height: "90vh" } } }}>
			<DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, overflow: "auto" }}>
				{isLoadingData ? (
					<Box
						sx={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							height: "100%",
						}}>
						<CircularProgress />
					</Box>
				) : (
					<>
						<Box>
							<Autocomplete
								multiple
								freeSolo
								options={searchEmailsHook.items
									.map((item) => item.email)
									.filter((email): email is string => email !== null)}
								value={recipientEmails}
								inputValue={recipientInputValue}
								loading={searchEmailsHook.loading}
								onChange={(_, newValue) => {
									// Filter out invalid emails on selection
									const validEmails = newValue.filter((email) => isValidEmail(email));
									setRecipientEmails(validEmails);
								}}
								onInputChange={(_, newInputValue) => {
									setRecipientInputValue(newInputValue);
								}}
								filterOptions={(x) => x} // Disable client-side filtering
								slotProps={{
									listbox: {
										onScroll: handleRecipientScroll,
										style: { maxHeight: 300, overflow: "auto" },
									},
								}}
								renderTags={(tagValue, getTagProps) =>
									tagValue.map((email, index) => {
										const { key, ...chipProps } = getTagProps({ index });
										const isValid = isValidEmail(email);
										return (
											<Chip
												key={key}
												label={email}
												size="small"
												color={isValid ? "default" : "error"}
												{...chipProps}
											/>
										);
									})
								}
								renderInput={(params) => (
									<TextField
										{...params}
										label={t("recipients")}
										placeholder={t("recipients-placeholder")}
										slotProps={{
											input: {
												...params.InputProps,
												endAdornment: (
													<>
														{searchEmailsHook.loading ? <CircularProgress size={20} /> : null}
														{params.InputProps.endAdornment}
													</>
												),
											},
										}}
									/>
								)}
								className="w-100 left"
							/>
						</Box>
						<Box>
							<Autocomplete
								multiple
								freeSolo
								options={searchEmailsHook.items
									.map((item) => item.email)
									.filter((email): email is string => email !== null)}
								value={ccEmails}
								inputValue={ccInputValue}
								loading={searchEmailsHook.loading}
								onChange={(_, newValue) => {
									const validEmails = newValue.filter((email) => isValidEmail(email));
									setCcEmails(validEmails);
								}}
								onInputChange={(_, newInputValue) => {
									setCcInputValue(newInputValue);
								}}
								filterOptions={(x) => x}
								slotProps={{
									listbox: {
										onScroll: handleCcScroll,
										style: { maxHeight: 300, overflow: "auto" },
									},
								}}
								renderTags={(tagValue, getTagProps) =>
									tagValue.map((email, index) => {
										const { key, ...chipProps } = getTagProps({ index });
										const isValid = isValidEmail(email);
										return (
											<Chip
												key={key}
												label={email}
												size="small"
												color={isValid ? "default" : "error"}
												{...chipProps}
											/>
										);
									})
								}
								renderInput={(params) => (
									<TextField
										{...params}
										label={t("cc")}
										placeholder={t("recipients-placeholder")}
										slotProps={{
											input: {
												...params.InputProps,
												endAdornment: (
													<>
														{searchEmailsHook.loading ? <CircularProgress size={20} /> : null}
														{params.InputProps.endAdornment}
													</>
												),
											},
										}}
									/>
								)}
								className="w-100 left"
							/>
						</Box>
						<Box>
							<Autocomplete
								multiple
								freeSolo
								options={searchEmailsHook.items
									.map((item) => item.email)
									.filter((email): email is string => email !== null)}
								value={bccEmails}
								inputValue={bccInputValue}
								loading={searchEmailsHook.loading}
								onChange={(_, newValue) => {
									const validEmails = newValue.filter((email) => isValidEmail(email));
									setBccEmails(validEmails);
								}}
								onInputChange={(_, newInputValue) => {
									setBccInputValue(newInputValue);
								}}
								filterOptions={(x) => x}
								slotProps={{
									listbox: {
										onScroll: handleBccScroll,
										style: { maxHeight: 300, overflow: "auto" },
									},
								}}
								renderTags={(tagValue, getTagProps) =>
									tagValue.map((email, index) => {
										const { key, ...chipProps } = getTagProps({ index });
										const isValid = isValidEmail(email);
										return (
											<Chip
												key={key}
												label={email}
												size="small"
												color={isValid ? "default" : "error"}
												{...chipProps}
											/>
										);
									})
								}
								renderInput={(params) => (
									<TextField
										{...params}
										label={t("bcc")}
										placeholder={t("recipients-placeholder")}
										slotProps={{
											input: {
												...params.InputProps,
												endAdornment: (
													<>
														{searchEmailsHook.loading ? <CircularProgress size={20} /> : null}
														{params.InputProps.endAdornment}
													</>
												),
											},
										}}
									/>
								)}
								className="w-100 left"
							/>
						</Box>
						<Box>
							<BoundTextField
								label={t("subject")}
								boundedValue={{
									value: invoiceData.subject || "",
									onChangeValue: (v: string) => {
										setInvoiceData((prev) => ({ ...prev, subject: v }));
									},
									errorKey: "",
								}}
								additionalProps={{ className: "w-100 left" }}
							/>
						</Box>

						{/* Email body editor */}
						<Box sx={{ flex: 1, minHeight: 300 }}>
							<Editor
								licenseKey="gpl"
								onEditorChange={(content) => {
									setInvoiceData((prev) => ({ ...prev, body: content }));
								}}
								init={{
									base_url: "/tinymce",
									suffix: ".min",
									width: "100%",
									height: "100%",
									menubar: false,
									plugins: [
										"advlist",
										"autolink",
										"lists",
										"link",
										"image",
										"charmap",
										"anchor",
										"searchreplace",
										"visualblocks",
										"code",
										"fullscreen",
										"insertdatetime",
										"media",
										"table",
										"preview",
										"wordcount",
									],
									toolbar:
										"undo redo | blocks | " +
										"bold italic forecolor | alignleft aligncenter " +
										"alignright alignjustify | bullist numlist outdent indent | " +
										"removeformat",
									content_style: "body { font-family:Helvetica,Arial,sans-serif; font-size:14px }",
									skin: false,
									content_css: false,
								}}
								value={invoiceData.body}
							/>
						</Box>

						<Box>
							<Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
								{t("attachments")}
							</Typography>
							<PDFAttachments
								existingAttachments={existingAttachments}
								onAttachmentsChange={handleAttachmentsChange}
								maxFileSize={25 * 1024 * 1024}
								allowMerge={true}
							/>
						</Box>
					</>
				)}
			</DialogContent>
			<DialogActions>
				<Button
					variant="outlined"
					data-testid="cancel-button"
					onClick={handleCloseInternal}
					disabled={isSending}>
					{t("cancel")}
				</Button>
				<Button
					variant="contained"
					data-testid="send-button"
					onClick={handleSendClick}
					disabled={recipientEmails.length === 0 || isSending}
					startIcon={isSending ? <CircularProgress size={16} color="inherit" /> : undefined}>
					{isSending ? t("sending") : t("send")}
				</Button>
			</DialogActions>
			<Snackbar
				open={errorMessage !== null}
				autoHideDuration={6000}
				onClose={handleCloseError}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
				<Alert onClose={handleCloseError} severity="error" sx={{ width: "100%" }}>
					{errorMessage}
				</Alert>
			</Snackbar>
		</Dialog>
	);
});
